import { useState, useEffect, useRef } from 'react';
import ErrorHandler from '@Root/ErrorHandler';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxOrg } from '@Hooks/reduxOrgHook/useReduxOrg';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import TeamService from '@Helpers/URLHelpers/AuthService/TeamService';
import OrgService from '@Helpers/URLHelpers/OrgService';
import ProjectService from '@Helpers/URLHelpers/ProjectService';
import UserInfoService from '@Helpers/URLHelpers/UserInfoService';
import { SnackbarManager } from '@CommonFunctionalDisplay/GlobalSnackbar';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { MdGroups, MdCheck } from 'react-icons/md';
import './CreateTeamModal.scss';

const isEmailLike = (str) => /^[^\s@]+@[^\s@]+/.test(str);

export default function CreateTeamModal({ open, onClose, existingTeams = [], existingUserIds = new Set(), onTeamCreated }) {
    const [step, setStep] = useState('create');
    const [teamName, setTeamName] = useState('');
    const [teamTag, setTeamTag] = useState('');
    const [nameError, setNameError] = useState('');
    const [tagError, setTagError] = useState('');
    const [creating, setCreating] = useState(false);
    const [createdTeam, setCreatedTeam] = useState(null);

    const [query, setQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [contributors, setContributors] = useState([]);
    const [addingContributors, setAddingContributors] = useState(false);

    const searchTimeout = useRef(null);
    const teamService = new TeamService();
    const orgService = new OrgService();
    const projectService = new ProjectService();
    const userInfoService = new UserInfoService();
    const errorHandler = new ErrorHandler();
    const { activeUserId } = useReduxActiveUser();
    const { currentOrgId } = useReduxOrg();
    const { currentProject } = useReduxProject();

    useEffect(() => {
        if (step !== 'addContributors') return;
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        const trimmed = query.trim();
        if (!trimmed || trimmed.length < 3 || isEmailLike(trimmed)) {
            setSearchResults([]);
            setDropdownOpen(trimmed.length > 0);
            return;
        }
        setDropdownOpen(true);
        searchTimeout.current = setTimeout(() => performSearch(trimmed), 350);
        return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
    }, [query, step]); // eslint-disable-line react-hooks/exhaustive-deps

    const validateTag = (tag) => /^[A-Z]{3,10}$/.test(tag);

    const validateTeam = (name) => {
        if (!existingTeams || existingTeams.length === 0) return true;
        return !existingTeams.some(t => t.teamName === name.trim());
    };

    const handleTagChange = (e) => {
        setTeamTag(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''));
        setTagError('');
    };

    const handleCreate = async () => {
        let valid = true;
        if (!teamName.trim()) {
            setNameError('Team name is required');
            valid = false;
        } else if (!validateTeam(teamName)) {
            setNameError('A team with this name already exists');
            valid = false;
        } else {
            setNameError('');
        }
        if (!validateTag(teamTag)) {
            setTagError('Must be 3–10 uppercase letters (A–Z only)');
            valid = false;
        } else {
            setTagError('');
        }
        if (!valid) return;

        try {
            setCreating(true);
            const userId = typeof activeUserId === 'string' ? JSON.parse(activeUserId) : activeUserId;
            const orgId = Number(currentOrgId);
            const res = await teamService.createTeam({
                teamName: teamName.trim(),
                tag: teamTag,
                orgId,
                creatorUserId: userId,
                ownerUserId: userId,
                parentType: 'org',
                parentId: orgId,
                visibility: 'private',
            });
            const created = res?.body ? JSON.parse(res.body) : res;
            setCreatedTeam(created);
            setStep('addContributors');
            onTeamCreated?.();
        } catch (err) {
            errorHandler.handleError(err);
            SnackbarManager.show?.('Failed to create team', 'error');
        } finally {
            setCreating(false);
        }
    };

    const performSearch = async (kw) => {
        try {
            setIsSearching(true);
            let data = await userInfoService.searchUserByKeyword(kw.toLowerCase());
            data = JSON.parse(data.body);
            const selfId = typeof activeUserId === 'string' ? JSON.parse(activeUserId) : activeUserId;
            setSearchResults((data || []).filter(u => Number(u.id) !== Number(selfId)));
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setIsSearching(false);
        }
    };

    const isQueued = (userId) => contributors.some(c => Number(c.id) === Number(userId));

    const handleSelectUser = (user) => {
        if (isQueued(user.id)) return;
        setContributors(prev => [...prev, user]);
        setQuery('');
        setSearchResults([]);
        setDropdownOpen(false);
    };

    const handleRemoveContributor = (userId) => {
        setContributors(prev => prev.filter(c => Number(c.id) !== Number(userId)));
    };

    const handleAddContributors = async () => {
        if (!createdTeam?.id || contributors.length === 0) return;
        try {
            setAddingContributors(true);
            const orgId = Number(currentOrgId);

            for (const user of contributors) {
                const userId = user.id;
                const alreadyInProject = existingUserIds.has(Number(userId));

                if (alreadyInProject) {
                    // Already a project member — just add to team
                    await teamService.addContributorToTeam({
                        childType: 'user',
                        userId,
                        teamId: createdTeam.id,
                        permissions: JSON.stringify(['read', 'write']),
                    });
                } else {
                    // Not yet in the project — add to org, project, then team
                    await orgService.addUserToOrg({
                        orgId,
                        permissions: JSON.stringify(['admin']),
                        userId,
                    });
                    await projectService.addContributorToProject({
                        childType: 'user',
                        userId,
                        projectId: currentProject?.id,
                        permissions: JSON.stringify(['admin']),
                    });
                    await teamService.addContributorToTeam({
                        childType: 'user',
                        userId,
                        teamId: createdTeam.id,
                        permissions: JSON.stringify(['read', 'write']),
                    });
                }
            }

            SnackbarManager.show?.(
                `${contributors.length} member${contributors.length !== 1 ? 's' : ''} added to ${createdTeam.teamName}`,
                'success'
            );
            onTeamCreated?.();
            handleClose();
        } catch (err) {
            errorHandler.handleError(err);
            SnackbarManager.show?.('Failed to add some members', 'error');
        } finally {
            setAddingContributors(false);
        }
    };

    const handleClose = () => {
        setStep('create');
        setTeamName('');
        setTeamTag('');
        setNameError('');
        setTagError('');
        setCreating(false);
        setCreatedTeam(null);
        setQuery('');
        setSearchResults([]);
        setIsSearching(false);
        setDropdownOpen(false);
        setContributors([]);
        setAddingContributors(false);
        onClose();
    };

    const trimmedQuery = query.trim();
    const showDropdown = dropdownOpen && trimmedQuery.length > 0 && step === 'addContributors';
    const showHint = !isEmailLike(trimmedQuery) && trimmedQuery.length > 0 && trimmedQuery.length < 3;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            className="createTeamModal"
            PaperProps={{ className: 'createTeamModal__paper' }}
        >
            <DialogTitle className="createTeamModal__title">
                <div className="createTeamModal__titleRow">
                    <div className="createTeamModal__titleIcon">
                        <MdGroups />
                    </div>
                    <div>
                        <div className="createTeamModal__titleText">
                            {step === 'create' ? 'Create New Team' : 'Team Created'}
                        </div>
                        {step === 'addContributors' && createdTeam && (
                            <div className="createTeamModal__titleSub">
                                Add members to <strong>{createdTeam.teamName}</strong>
                            </div>
                        )}
                    </div>
                </div>
            </DialogTitle>

            <DialogContent className="createTeamModal__content">

                {/* ── Step 1: Create ── */}
                {step === 'create' && (
                    <>
                        <p className="createTeamModal__hint">
                            Give your team a name and a short tag. The tag is used for quick identification and must be 3–10 uppercase letters.
                        </p>
                        <div className="createTeamModal__fields">
                            <div className="createTeamModal__fieldGroup">
                                <label className="createTeamModal__label">Team Name</label>
                                <TextField
                                    placeholder="e.g. Support Team"
                                    value={teamName}
                                    onChange={(e) => { setTeamName(e.target.value); setNameError(''); }}
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    error={!!nameError}
                                    helperText={nameError}
                                    className="createTeamModal__input"
                                    autoFocus
                                    onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                                />
                            </div>
                            <div className="createTeamModal__fieldGroup">
                                <label className="createTeamModal__label">Tag</label>
                                <TextField
                                    placeholder="e.g. SUP"
                                    value={teamTag}
                                    onChange={handleTagChange}
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    error={!!tagError}
                                    helperText={tagError || '3–10 uppercase letters only'}
                                    inputProps={{ maxLength: 10 }}
                                    className="createTeamModal__input createTeamModal__input--tag"
                                    onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                                />
                            </div>
                        </div>
                    </>
                )}

                {/* ── Step 2: Add Contributors ── */}
                {step === 'addContributors' && (
                    <>
                        <div className="createTeamModal__successBanner">
                            <span className="createTeamModal__successIcon"><MdCheck /></span>
                            <span>
                                <strong>{createdTeam?.teamName}</strong>
                                {createdTeam?.tag ? ` (${createdTeam.tag})` : ''} was created successfully
                            </span>
                        </div>

                        <p className="createTeamModal__hint">
                            Search for users to add to this team. You can skip this step and add members later.
                        </p>

                        {contributors.length > 0 && (
                            <div className="createTeamModal__pills">
                                {contributors.map(user => (
                                    <div key={user.id} className="createTeamModal__pill">
                                        <div className="createTeamModal__pill__avatar">
                                            {(user.fullName || user.userName || 'U').slice(0, 1).toUpperCase()}
                                        </div>
                                        <span className="createTeamModal__pill__name">
                                            {user.fullName || user.userName}
                                        </span>
                                        <button
                                            className="createTeamModal__pill__remove"
                                            onClick={() => handleRemoveContributor(user.id)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="createTeamModal__searchWrapper">
                            <TextField
                                placeholder="Search by name..."
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                variant="outlined"
                                fullWidth
                                size="small"
                                autoComplete="off"
                                className="createTeamModal__input"
                                InputProps={{
                                    endAdornment: isSearching
                                        ? <CircularProgress size={14} style={{ color: 'var(--color-primaryGrey)', flexShrink: 0 }} />
                                        : null,
                                }}
                            />
                            {showHint && (
                                <div className="createTeamModal__inputHint">
                                    Type {3 - trimmedQuery.length} more character{3 - trimmedQuery.length !== 1 ? 's' : ''} to search
                                </div>
                            )}
                            {showDropdown && (
                                <div className="createTeamModal__dropdown">
                                    {isSearching && (
                                        <div className="createTeamModal__dropdownStatus">
                                            <CircularProgress size={13} />
                                            <span>Searching...</span>
                                        </div>
                                    )}
                                    {!isSearching && searchResults.length === 0 && (
                                        <div className="createTeamModal__dropdownStatus">No users found</div>
                                    )}
                                    {!isSearching && searchResults.map(user => {
                                        const queued = isQueued(user.id);
                                        return (
                                            <button
                                                key={user.id}
                                                className={`createTeamModal__dropdownItem${queued ? ' disabled' : ''}`}
                                                onClick={() => !queued && handleSelectUser(user)}
                                                type="button"
                                                disabled={queued}
                                            >
                                                <div className="createTeamModal__dropdownItem__avatar">
                                                    {(user.fullName || user.userName || 'U').slice(0, 1).toUpperCase()}
                                                </div>
                                                <div className="createTeamModal__dropdownItem__info">
                                                    <div className="createTeamModal__dropdownItem__name">
                                                        {user.fullName || user.userName}
                                                    </div>
                                                    <div className="createTeamModal__dropdownItem__email">
                                                        {user.email || user.userName}
                                                    </div>
                                                </div>
                                                {queued && (
                                                    <span className="createTeamModal__dropdownItem__statusTag">
                                                        Added
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </DialogContent>

            <DialogActions className="createTeamModal__actions">
                {step === 'create' && (
                    <>
                        <Button onClick={handleClose} variant="text" disabled={creating}>
                            Cancel
                        </Button>
                        <Button onClick={handleCreate} variant="contained" disabled={creating}>
                            {creating
                                ? <><CircularProgress size={13} style={{ marginRight: 6 }} />Creating...</>
                                : 'Create Team'}
                        </Button>
                    </>
                )}
                {step === 'addContributors' && (
                    <>
                        <Button onClick={handleClose} variant="text" disabled={addingContributors}>
                            {contributors.length === 0 ? 'Done' : 'Skip'}
                        </Button>
                        {contributors.length > 0 && (
                            <Button
                                onClick={handleAddContributors}
                                variant="contained"
                                disabled={addingContributors}
                            >
                                {addingContributors
                                    ? <><CircularProgress size={13} style={{ marginRight: 6 }} />Adding...</>
                                    : `Add ${contributors.length} Member${contributors.length !== 1 ? 's' : ''}`}
                            </Button>
                        )}
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}
